import React, { useState } from 'react';
import {
  MessageSquare, Zap, Globe, Code, TrendingUp, Cpu, CheckCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const services = [
  { id: 1, title: "AI Sales Automation", description: "Intelligent systems that respond to enquiries instantly, qualify leads automatically, and follow up 24/7 to book more appointments.", icon: MessageSquare, gradient: "from-cyan-500 to-blue-600", stat: "24/7 lead response" },
  { id: 2, title: "Revenue Growth Systems", description: "Automated income-generating workflows that turn missed enquiries into booked customers without manual intervention.", icon: Zap, gradient: "from-purple-500 to-indigo-600", stat: "More conversions" },
  { id: 3, title: "Business Websites", description: "High-converting websites designed to capture leads and integrate seamlessly with your AI sales systems.", icon: Globe, gradient: "from-emerald-500 to-teal-600", stat: "Mobile-optimised" },
  { id: 4, title: "Custom Software", description: "Bespoke software solutions tailored to your business processes, from mobile apps to enterprise platforms.", icon: Code, gradient: "from-orange-500 to-amber-600", stat: "Built for you" },
  { id: 5, title: "Lead Generation", description: "Strategic campaigns to attract qualified prospects into your automated sales funnel for consistent growth.", icon: TrendingUp, gradient: "from-pink-500 to-rose-600", stat: "Quality leads" },
  { id: 6, title: "AI Integrations", description: "Connect AI capabilities to your existing CRM, calendar, and business tools for seamless automation.", icon: Cpu, gradient: "from-violet-500 to-purple-600", stat: "Works with your tools" }
];

const ServicesSection = ({ onBookNow }) => {
  const { isDarkMode } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="services" className={`relative py-24 transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-testid="services-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, ${isDarkMode ? 'rgba(0, 180, 216, 0.1)' : 'rgba(0, 180, 216, 0.05)'} 1px, transparent 1px),
                           linear-gradient(to bottom, ${isDarkMode ? 'rgba(0, 180, 216, 0.1)' : 'rgba(0, 180, 216, 0.05)'} 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`text-sm uppercase tracking-[0.3em] font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>Our Services</span>
          <h2 className={`text-4xl md:text-5xl font-extralight mt-4 mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Income Growth
            <span className="font-normal bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"> Software Solutions</span>
          </h2>
          <p className={`text-lg max-w-3xl mx-auto leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Software that turns missed enquiries into booked customers—automatically responding, qualifying, and following up 24/7.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isHovered = hoveredIndex === index;

            return (
              <div
                key={service.id}
                className="group relative"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                data-testid={`service-card-${service.id}`}
              >
                {/* Hover Glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-xl blur-xl transition-opacity duration-500 ${isHovered ? 'opacity-25' : 'opacity-0'}`} />

                {/* Card */}
                <div className={`relative p-6 rounded-xl border-2 bg-white transition-all duration-500 h-full shadow-sm hover:shadow-lg ${isHovered ? 'border-transparent' : 'border-gray-100'}`}>
                  {/* Colored Top Border */}
                  <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-xl bg-gradient-to-r ${service.gradient}`} />

                  {/* Icon */}
                  <div className="mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${service.gradient} shadow-md`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-base font-semibold text-gray-900 mb-2 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>

                  {/* Stat Badge */}
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-medium text-emerald-600">{service.stat}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            Not Sure What You Need?
          </h3>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
            Book a free consultation and we'll create a custom package tailored to your business goals and budget.
          </p>
          <button
            onClick={onBookNow}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:opacity-90 transition-all shadow-lg shadow-purple-500/30"
            data-testid="services-book-call"
          >
            FREE QUOTE
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
